<template>
  <component
    :is="tagName"
    class="base-button"
    :class="classObject"
    :to="link"
    :type="butonElemType"
    :disabled="isDisabled"
    :aria-pressed="ariaPressed"
    @click="handleClick"
  >
    <slot name="arrowImage"></slot>
    <slot name="iconImage"></slot>
    <slot name="taggedText"></slot>
    <template v-if="!slots.iconImage && !slots.taggedText">
      {{ text }}
    </template>
    <loading scope="single" :loaded="!processing" />
  </component>
</template>

<script setup>
const slots = useSlots()
import Loading from '@/components/Loading.vue'

const props = defineProps({
  /**
   * 値（リンクボタンには不要）
   */
    value: {
    type: String,
    default: '',
  },
  /**
   * テキスト
   */
  text: {
    type: String,
    default: null,
  },
  /**
   * リンク先パス
   */
  link: {
    type: String,
    default: '',
  },
  /**
   * アイコン名
   * @values location, zoom
   */
  icon: {
    type: String,
    default: '',
  },
  /**
   * 色
   * @values primary, secondary, success, danger, warning, info, light, dark(bootstrapの色指定を参考:https://getbootstrap.jp/docs/5.0/components/buttons/), navigatiom
   */
  buttonColor: {
    type: String,
    default: 'primary',
  },
  /**
   * なりゆき幅
   */
  contentFit: {
    type: Boolean,
    default: false,
  },
  /**
   * オーバーレイの有効状態（アイコンのみボタン用）
   */
  overlaid: {
    type: Boolean,
    default: false,
  },
  /**
   * トグルボタンとして使うかどうか（リンクボタンには不要）
   */
  toggle: {
    type: Boolean,
    default: false,
  },
  /**
   * トグルボタンの押下状態
   */
  pressed: {
    type: Boolean,
    default: false,
  },
  /**
   * 無効状態
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * 枠線を残してボタンの背景色を透過状態
   */
  outlined: {
    type: Boolean,
    default: false,
  },
  /**
   * 影の状態(デフォルトはtrue)
   */
  shadow: {
    type: Boolean,
    default: true,
  },
  /**
   * 角丸状態(デフォルトはtrue)
   */
  rounded: {
    type: Boolean,
    default: true,
  },
  /**
   * 処理中
   */
  processing: {
    type: Boolean,
    default: false,
  },
  newButton: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const isLinkButton = computed(() => {
  return props.link
})

const tagName = computed(() => {
  if (isLinkButton.value) return resolveComponent('NuxtLink')
  return 'button'
})

const butonElemType = computed(() => {
  isLinkButton.value ? null : 'button'
})

const classObject = computed(() => {
  return {
    secondary: props.buttonColor === 'secondary',
    success: props.buttonColor === 'success',
    danger: props.buttonColor === 'danger',
    warning: props.buttonColor === 'warning',
    info: props.buttonColor === 'info',
    light: props.buttonColor === 'light',
    dark: props.buttonColor === 'dark',
    navigation: props.buttonColor === 'navigation',
    icon: props.icon,
    location: props.icon === 'location',
    zoom: props.icon === 'zoom',
    'icon-only': slots.iconImage,
    'icon-overlaid': props.overlaid,
    'content-fit': props.contentFit,
    toggle: props.toggle,
    pressed: props.pressed,
    shadow: props.shadow,
    outlined: props.outlined,
    rounded: props.rounded,
    processing: props.processing,
    new: props.newButton,
  }
})

const ariaPressed = computed(() => {
  return props.toggle && !isLinkButton.value ? String(props.pressed) : null
})

const isDisabled = computed(() => {
  if (props.disabled || props.processing) {
    return true
  } else {
    return null
  }
})

const handleClick = () => {
  if (!isLinkButton.value) {
    const data = props.toggle
      ? { value: props.value, pressed: !props.pressed }
      : { value: props.value }
    emit('click', data)
  }
}

</script>

<style lang="scss" scoped>
.base-button {
  border: 0.05rem solid $color-accent;
  width: 100%;
  height: 4rem;
  color: $color-white;
  background: $color-accent;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0;
  cursor: pointer;

  @media screen and (min-width: $tablet-breakpoint) {
    font-size: 1.4rem;
    line-height: calc(40 / 14);
  }

  ::v-deep svg path {
    fill: currentColor !important;
  }

  // &.secondary {
  // }
  // &.success {
  // }
  // &.caution {
  // }
  // &.warning {
  // }
  // &.info {
  // }

  &.new {
    height: 4.8rem;
  }
  &.light {
    border: none;
    background-color: $color-white;
    color: $color-main-text;
    position: relative;

    &.rounded::before {
      content: '';
      box-shadow: 0 -0.05rem 0 0.05rem $color-line,
        0 0.05rem 0 0.05rem $color-line;
      width: 100%;
      height: 100%;
      position: absolute;
      border-radius: 3em;
    }

    ::v-deep svg path {
      fill: $color-main !important;
    }
  }

  &.navigation {
    border: none;
    background-color: $color-white;
    color: $color-main-text;

    ::v-deep svg path {
      fill: $color-gray-text !important;
    }
  }
  // &.dark {
  // }

  &.outlined {
    color: $color-accent;
    background-color: $color-white;

    ::v-deep svg path {
      fill: currentColor !important;
    }

    // &.secondary {
    // }
    // &.success {
    // }
    // &.caution {
    // }
    // &.warning {
    // }
    // &.info {
    // }
    &.light {
      background-color: transparent;
      color: $color-line;
    }
    // &.dark {
    // }
  }

  &.shadow {
    box-shadow: 0 0.2rem 0.4rem 0 rgba(0, 0, 0, 0.08);
  }

  &.rounded {
    border-radius: 3em;
  }

  &.content-fit {
    width: auto;
    padding: 0 3.2rem;
  }

  // トグルボタンは button-color の影響を受けてはならない（固定スタイル）
  &.toggle {
    border-color: transparent;
    // TODO 色は要相談
    color: $color-white;
    // TODO 色は要相談
    background-color: $color-main;
  }

  &[disabled] {
    /* opacity: 0.5; */
    cursor: not-allowed;
    background-color: #f4f4f4;
    border-color: #f4f4f4;
    color: #aaa;
  }

  &.icon {
    &::before {
      margin-right: 1rem;
      // font-size: 12px;
      font-size: 1.8rem;
      font-family: 'Tableva'; // stylelint-disable-line font-family-no-missing-generic-family-keyword
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
      line-height: 1;

      @media screen and (min-width: $tablet-breakpoint) {
        // font-size: 15px;
        font-size: 18px;
        margin-right: 6.5px;
      }
    }
  }

  &.location {
    &::before {
      content: '\e91b';
    }
  }

  &.zoom {
    &::before {
      content: '\e91c';
    }
  }

  &.icon-only {
    border-radius: 50%;
    padding: 1em;
    width: 4rem;
    height: 4rem;
    font-size: 1rem;

    @media screen and (min-width: $tablet-breakpoint) {
      width: 40px;
      height: 40px;
    }

    &.icon-overlaid {
      border: none;
      width: 3.5rem;
      height: 3.5rem;
    }

    &[disabled]:not(.icon-overlaid) {
      border-color: #aeaeb2;
      background-color: #aeaeb2;
    }
  }

  &.processing {
    position: relative;
  }
}
</style>
